import {
  QrCodeIcon,
  MapPinIcon,
  FlagIcon as FlagSolidIcon,
} from '@heroicons/react/20/solid';
import { NeedsApprovalCheckEvent } from '../api/types';
import { formatClock } from '../util/dateFormat';
import { oneDecimal } from '../util/numberFormat';

type Props = {
  event: NeedsApprovalCheckEvent;
  timezone?: string | null;
  testId: string;
};

/**
 * One side of a worker's timesheet: time, method icon, distance underneath.
 *
 * ONE component for both sides on purpose (GRAVY-10366). The AC's rule is that
 * "only the flagged side changes" — rendering check-in and check-out from two
 * different components is exactly how that gets honoured in one place and
 * missed in the other.
 *
 * ## Icons (D3, reopened by GRAVY-10825)
 *
 *   | side                         | icon                     |
 *   | ---------------------------- | ------------------------ |
 *   | `qr`                         | QR grid                  |
 *   | non-QR, coordinates captured | map pin — "GPS check-in" |
 *   | non-QR, no coordinates       | none                     |
 *   | flagged, any method          | red flag, overriding all |
 *
 * D3 originally shipped NO pin, on the grounds that nothing writes
 * `verification_method = 'gps'`. True of that column, and beside the point: it
 * only ever holds `qr`, `skipped`, `manual_override` or null, so it cannot tell
 * a worker's ordinary in-app check-in from an hours-entered-by-ops timesheet
 * either. The effect was that the COMMON path — check in through the app, GPS
 * and all — rendered with no icon at all, which is what GRAVY-10825 caught in
 * production.
 *
 * `has_coordinates` is the fact the column could not carry. An in-app check-in
 * cannot happen without coordinates (`Shifts::ManualCheckinService` refuses
 * one), and an ops-typed time deliberately has none
 * (`Timesheets::OverrideCheckinService`), so the pin marks a real location
 * capture and still never asserts one that did not happen. The last row of the
 * table is what remains of D3 and is intentional, not an oversight.
 *
 * Older graphy leaves `has_coordinates` undefined, which reads as false — the
 * pre-10825 rendering, so a frontend deployed first degrades rather than lies.
 */
const CheckEventCell = ({ event, timezone, testId }: Props) => {
  const flagged = event.flagged;
  const isQr = event.verification_method === 'qr';
  const isGps = !isQr && event.has_coordinates === true;

  return (
    <div className="dashv2-nacheck" data-testid={testId}>
      <div className="dashv2-nacheck-top">
        <span className="dashv2-nacheck-time">
          {event.at ? formatClock(event.at, timezone) : '—'}
        </span>
        {flagged ? (
          <FlagSolidIcon
            className="dashv2-nacheck-icon is-flagged"
            data-testid={`${testId}-flag`}
            aria-label="Location flag"
          />
        ) : isQr ? (
          <QrCodeIcon
            className="dashv2-nacheck-icon"
            data-testid={`${testId}-qr`}
            aria-label="QR scan"
          />
        ) : (
          isGps && (
            <MapPinIcon
              className="dashv2-nacheck-icon"
              data-testid={`${testId}-pin`}
              aria-label="GPS check-in"
            />
          )
        )}
      </div>
      {/* An em dash, not "0.0 mi away", which would claim they stood on the
          spot. Null is NOT the same question `has_coordinates` answers above: a
          distance also needs a geocoded venue anchor and a measurement pass, so
          a side can be located and still have no distance (GRAVY-10824). Such a
          side shows a pin over an em dash, and that is correct. */}
      <p
        className={`dashv2-nacheck-distance${flagged ? ' is-flagged' : ''}`}
        data-testid={`${testId}-distance`}
      >
        {event.distance_miles == null
          ? '—'
          : `${oneDecimal(event.distance_miles)} mi away`}
      </p>
    </div>
  );
};

export default CheckEventCell;
